
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useOrganization } from '@/contexts/OrganizationContext';

export interface EventVendor {
  id: string;
  event_id: string;
  vendor_id: string;
  cost: number;
  payment_terms: string;
  status: string;
  notes: string;
  organization_id: string;
  created_at: string;
  updated_at: string;
  vendor: {
    id: string;
    name: string;
    category: string;
    contact_person: string;
    phone: string;
    email: string;
    description: string;
  };
}

export const useEventVendors = (eventId: string) => {
  return useQuery({
    queryKey: ['event_vendors', eventId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('event_vendors')
        .select(`
          *,
          vendor:vendors(
            id,
            name,
            category,
            contact_person,
            phone,
            email,
            description
          )
        `)
        .eq('event_id', eventId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as EventVendor[];
    },
    enabled: !!eventId,
  });
};

export const useAddEventVendor = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { currentOrganization } = useOrganization();

  return useMutation({
    mutationFn: async (vendorData: {
      event_id: string;
      vendor_id: string;
      cost: number;
      payment_terms: string;
      status: string;
      notes?: string;
    }) => {
      if (!currentOrganization?.id) throw new Error('No organization selected');

      const { data, error } = await supabase
        .from('event_vendors')
        .insert([{
          ...vendorData,
          organization_id: currentOrganization.id
        }])
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['event_vendors', data.event_id] });
      toast({
        title: "Success",
        description: "Vendor added to event successfully",
      });
    },
    onError: (error) => {
      console.error('Event vendor creation error:', error);
      toast({
        title: "Error",
        description: "Failed to add vendor to event",
        variant: "destructive",
      });
    },
  });
};

export const useUpdateEventVendor = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ id, vendor, ...vendorData }: Partial<EventVendor> & { id: string }) => {
      // First update the event vendor record
      const { data: eventVendorData, error: eventVendorError } = await supabase
        .from('event_vendors')
        .update({
          cost: vendorData.cost,
          payment_terms: vendorData.payment_terms,
          status: vendorData.status,
          notes: vendorData.notes
        })
        .eq('id', id)
        .select()
        .single();

      if (eventVendorError) throw eventVendorError;

      // Then update the vendor record
      if (vendor) {
        const { error: vendorError } = await supabase
          .from('vendors')
          .update({
            name: vendor.name,
            category: vendor.category as 'Marketing' | 'Other' | 'F&B' | 'Decor' | 'Photography/Videography' | 'Entertainment' | 'Catering' | 'Venue' | 'Audio/Visual' | 'Security' | 'Transportation' | 'Flowers' | 'Lighting',
            contact_person: vendor.contact_person,
            phone: vendor.phone,
            email: vendor.email,
            description: vendor.description
          })
          .eq('id', vendor.id);

        if (vendorError) throw vendorError;
      }

      return eventVendorData;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['event_vendors', data.event_id] });
      toast({
        title: "Success",
        description: "Vendor updated successfully",
      });
    },
    onError: (error) => {
      console.error('Event vendor update error:', error);
      toast({
        title: "Error",
        description: "Failed to update vendor",
        variant: "destructive",
      });
    },
  });
};

export const useDeleteEventVendor = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ id, eventId }: { id: string; eventId: string }) => {
      const { error } = await supabase
        .from('event_vendors')
        .delete()
        .eq('id', id);

      if (error) throw error;
      return { eventId };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['event_vendors', data.eventId] });
      toast({
        title: "Success",
        description: "Vendor removed from event successfully",
      });
    },
    onError: (error) => {
      console.error('Event vendor deletion error:', error);
      toast({
        title: "Error",
        description: "Failed to remove vendor from event",
        variant: "destructive",
      });
    },
  });
};
